/**
 * CrossTableReadiness — per-format wizard readiness chip.
 *
 * Calls useCrossTableReadiness(template) and renders a Badge:
 *   - fallback:true  → "Using shipped defaults" (honest — T-22-10)
 *   - missing empty  → success "loaded ✓ (n tables)"
 *   - missing > 0    → warning "partial — missing: X, Y"
 *   - loaded empty   → neutral "no cross-tables uploaded" + link to Profile
 *
 * NON-BLOCKING: chip only; never disables generation controls.
 * On error: silent (no chip, no crash).
 * On loading: small Spinner only.
 */
import { useTranslation } from "react-i18next";
import { Badge } from "../../components/Badge";
import { Spinner } from "../../components/Spinner";
import { useCrossTableReadiness } from "../../api/orgXtables";

export function CrossTableReadiness({
  template,
  configId,
}: {
  template?: string;
  /** When a saved config is in context, its readiness is book-aware (attached code book +
   *  per-config overrides) — threaded to the readiness API as config_id. */
  configId?: string;
}) {
  const { t } = useTranslation();
  const readinessQ = useCrossTableReadiness(template, configId);

  // When no template provided, render nothing
  if (!template) return null;

  // Loading state
  if (readinessQ.isLoading) {
    return <Spinner size="sm" label={t("common.loading")} />;
  }

  // Error: silent — non-blocking
  if (readinessQ.isError || !readinessQ.data) {
    return null;
  }

  const { required, loaded, missing, fallback } = readinessQ.data;

  // Formats that require no cross-tables get no chip at all (a 'none' warning
  // here would be false — there is nothing to upload).
  if (!required || required.length === 0) return null;

  // T-22-10: fallback must be phrased honestly — not as "your lab uploaded"
  if (fallback) {
    return (
      <span data-testid="readiness-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Badge tone="default" title={t("generate.readiness.fallback")}>
          {t("generate.readiness.fallback")}
        </Badge>
      </span>
    );
  }

  // Loaded state: missing is empty AND we have loaded tables
  if (missing.length === 0 && loaded.length > 0) {
    return (
      <span data-testid="readiness-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Badge tone="success">
          {t("generate.readiness.loaded", { count: loaded.length })}
        </Badge>
      </span>
    );
  }

  // Partial state: some loaded, some missing
  if (missing.length > 0 && loaded.length > 0) {
    return (
      <span data-testid="readiness-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Badge tone="warning" title={t("generate.readiness.partial", { missing: missing.join(", ") })}>
          {t("generate.readiness.partial", { missing: missing.slice(0, 2).join(", ") + (missing.length > 2 ? `… +${missing.length - 2}` : "") })}
        </Badge>
        {" "}
        <a href="/profile#cross-tables" className="faint" style={{ fontSize: "0.85em" }}>
          {t("generate.readiness.manageLink")}
        </a>
      </span>
    );
  }

  // None state: no loaded tables at all
  return (
    <span data-testid="readiness-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Badge tone="default">
        {t("generate.readiness.none")}
      </Badge>
      {" "}
      <a href="/profile#cross-tables" className="faint" style={{ fontSize: "0.85em" }}>
        {t("generate.readiness.manageLink")}
      </a>
    </span>
  );
}
